<template>
  <div>
    <h3 class="pt-3">{{ title }}</h3>
    <p>{{ introductionText }}</p>
    <b-card no-body class="mt-3">
      <b-tabs ref="tabs" v-model="currentPage" card>
        <b-tab
          ref="info"
          :title="$t('ombudsman.information')"
          :disabled="recordCreated || uploading"
        >
          <p>{{ instructionText }}</p>
          <hr />
          <b-form no-validate>
            <b-row>
              <b-col cols="12" lg="3">
                <b-form-group :label="i18n.fullName">
                  <b-form-input
                    v-model="record.person.fullName"
                    v-validate="rules.person.fullName"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidFullName"
                    name="fullName"
                    :state="validateState('fullName')"
                  />
                  <b-form-invalid-feedback id="invalidFullName">
                    {{ errors.first("fullName") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="3">
                <b-form-group :label="i18n.cpf">
                  <b-form-input
                    ref="cpf"
                    v-model="record.person.cpf"
                    v-mask="'###.###.###-##'"
                    v-validate="rules.person.cpf"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidCPF"
                    name="cpf"
                    :state="validateState('cpf')"
                  />
                  <b-form-invalid-feedback id="invalidCPF">
                    {{ errors.first("cpf") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="3">
                <b-form-group :label="i18n.birthday">
                  <b-form-input
                    v-model="record.person.birthday"
                    v-validate="rules.person.birthday"
                    type="date"
                    class="form-control"
                    aria-describedby="invalidBirthday"
                    name="birthday"
                    :state="validateState('birthday')"
                  />
                  <b-form-invalid-feedback id="invalidBirthday">
                    {{ errors.first("birthday") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" lg="3">
                <b-form-group :label="i18n.email">
                  <b-form-input
                    v-model="record.person.email"
                    v-validate="rules.person.email"
                    type="email"
                    class="form-control"
                    aria-describedby="invalidEmail"
                    name="email"
                    :state="validateState('email')"
                  />
                  <b-form-invalid-feedback id="invalidEmail">
                    {{ errors.first("email") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="3">
                <b-form-group :label="i18n.phone">
                  <b-form-input
                    ref="phone"
                    v-model="record.person.phone"
                    v-mask="['(##) ####-####', '(##) #####-####']"
                    v-validate="rules.person.phone"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidPhone"
                    name="phone"
                    :state="validateState('phone')"
                  />
                  <b-form-invalid-feedback id="invalidPhone">
                    {{ errors.first("phone") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <template v-for="opt in ombudsmanClassificationOptions">
                <b-col :key="opt.name" cols="12" lg="4" md="6">
                  <b-form-group
                    :label="
                      opt.group ? opt.name : $t(`classification.${opt.name}`)
                    "
                  >
                    <b-form-select
                      v-model="
                        record.classification[
                          opt.group ? `custom_${opt.group}` : opt.name
                        ]
                      "
                      v-validate="{ required: true }"
                      :options="
                        classificationOptions[
                          opt.group ? `custom_${opt.group}` : opt.name
                        ]
                      "
                      :aria-describedby="`invalid-${opt.name}`"
                      :name="
                        opt.group ? opt.name : $t(`classification.${opt.name}`)
                      "
                      :state="
                        validateState(
                          opt.group
                            ? opt.name
                            : $t(`classification.${opt.name}`)
                        )
                      "
                    ></b-form-select>
                    <b-form-invalid-feedback :id="`invalid-${opt.name}`">
                      {{
                        errors.first(
                          opt.group
                            ? opt.name
                            : $t(`classification.${opt.name}`)
                        )
                      }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </b-col>
              </template>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group
                  :label="$t('ombudsman.fields.text')"
                  :description="$t('ombudsman.fieldsDesc.text')"
                >
                  <b-form-textarea
                    v-model="record.text"
                    v-validate="rules.text"
                    class="form-control"
                    aria-describedby="invalidText"
                    name="recordText"
                    :rows="4"
                    :state="validateState('recordText')"
                  />
                  <b-form-invalid-feedback id="invalidText">
                    {{ errors.first("recordText") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group :label="$t('ombudsman.fields.relatedInfo')">
                  <b-form-textarea
                    v-model="record.relatedInfo"
                    v-validate="rules.relatedInfo"
                    class="form-control"
                    aria-describedby="invalidRelatedInfo"
                    name="relatedInfo"
                    :rows="4"
                    :state="validateState('relatedInfo')"
                  />
                  <b-form-invalid-feedback id="invalidRelatedInfo">
                    {{ errors.first("relatedInfo") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
        </b-tab>
        <b-tab
          :title="$t('ombudsman.attachments')"
          :disabled="(!infoValidated || recordCreated) && !uploading"
        >
          <h5 class="pt-1 pb-1">{{ $t("ombudsman.attachmentInfo") }}</h5>
          <multiple-file-upload
            ref="upload"
            :post-action="upload.postAction"
            :data="upload.data"
            :disabled="uploading || uploadComplete"
            @upload-complete="onUploadComplete"
          />
        </b-tab>
        <b-tab
          :title="$t('ombudsman.confirmation')"
          :disabled="!infoValidated || uploading"
        >
          <template v-if="recordCreated">
            <h3>{{ $t("ombudsman.success") }}</h3>
            <h5>
              {{ $t("ombudsman.successInfo1") }}<br />{{
                $t("ombudsman.successInfo2")
              }}
            </h5>
            <h5>
              {{ $t("labels.protocol") }}:
              <strong>{{ context.protocol }}</strong>
            </h5>
          </template>

          <template v-else>
            <b-form-group>
              <b-form-checkbox
                v-model="record.lgpdAccepted"
                v-validate="rules.lgpdAccepted"
                class="mb-auto"
                :value="true"
                :unchecked-value="false"
                name="lgpdAccepted"
                :state="validateState('lgpdAccepted')"
                >{{ $t("ombudsman.termsInfo") }}
              </b-form-checkbox>
            </b-form-group>
            <b-form-group>
              <b-form-checkbox
                v-model="record.termsAccepted"
                v-validate="rules.termsAccepted"
                class="mb-auto"
                :value="true"
                :unchecked-value="false"
                name="termsAccepted"
                :state="validateState('termsAccepted')"
                >{{ $t("ombudsman.fields.termsAccepted") }}
              </b-form-checkbox>
            </b-form-group>
          </template>
        </b-tab>
      </b-tabs>
      <template slot="footer">
        <template v-if="recordCreated">
          <b-button
            variant="primary"
            class="mr-1"
            :disabled="loading"
            @click="$router.replace('/')"
            ><i class="fas fa-home" aria-hidden="true" />
            {{ $t("labels.home") }}
          </b-button>
        </template>
        <template v-else>
          <b-button class="mr-1" :disabled="loading" @click="previousPage">{{
            $t("labels.back")
          }}</b-button>
          <b-button
            variant="primary"
            :disabled="
              loading || (currentPage === 1 && $refs.upload.hasInvalidFiles)
            "
            @click="nextPage"
          >
            {{ currentPage === 2 ? $t("labels.finish") : $t("labels.next") }}
          </b-button>
        </template>
      </template>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ValidatorDictionary } from "@/i18n";
import { mask } from "vue-the-mask";
import MultipleFileUpload from "../../../components/MultipleFileUpload";
import { sendOmbudsmanRecord } from "@/api/main";

export default {
  name: "CompanyOmbudsman",
  directives: { mask },
  components: { MultipleFileUpload },
  data() {
    return {
      record: {
        person: {},
        classification: {
          ombudsmanNature: null,
        },
      },
      currentPage: 0,
      totalPages: 3,
      infoValidated: false,
      loading: false,
      uploading: false,
      uploadComplete: false,
      upload: {
        postAction: "",
        data: {},
      },
      context: {
        protocol: null,
      },
      recordCreated: false,
    };
  },
  computed: {
    ...mapGetters([
      "portal",
      "classificationOptions",
      "enabledClassificationOptions",
    ]),
    title() {
      const portal = this.portal;
      if (portal && portal.ombudsman && portal.ombudsman.title)
        return portal.ombudsman.title;
      return this.$i18n.t("ombudsman.title");
    },
    introductionText() {
      const portal = this.portal;
      if (portal && portal.ombudsman && portal.ombudsman.introductionText)
        return portal.ombudsman.introductionText;
      return this.$i18n.t("ombudsman.introduction");
    },
    instructionText() {
      const portal = this.portal;
      if (portal && portal.ombudsman && portal.ombudsman.instructionText)
        return portal.ombudsman.instructionText;
      return this.$i18n.t("ombudsman.instruction");
    },
    rules() {
      return {
        person: {
          fullName: { required: true },
          cpf: { required: false, cpf: true },
          birthday: { required: false },
          email: { required: true, email: true },
          phone: { required: false },
        },
        text: { required: true },
        relation: { required: false },
        termsAccepted: { required: true, acceptance: true },
        lgpdAccepted: { required: true, acceptance: true },
        communicationNature: { required: true },
      };
    },

    i18n() {
      return ValidatorDictionary[this.$i18n.locale].attributes;
    },

    ombudsmanClassificationOptions() {
      const allowedTypes = [13, 14, 15, 16];
      return this.enabledClassificationOptions.filter((opt) => {
        return opt.group !== undefined || allowedTypes.includes(opt.type);
      });
    },
  },

  created() {
    const updateEls = ["cpf", "phone"];
    this.$nextTick(() => {
      for (const id of updateEls) {
        this.$refs[id].$el.oninput({ isTrusted: true });
      }
      this.$validator.reset();
    });
  },
  methods: {
    sanitizePayload(payload) {
      if (payload.person.phone) {
        payload.person.phone = payload.person.phone.replace(/[^\d]/g, "");
        if (!payload.person.phone) delete payload.person.phone;
      }
      if (payload.person.cpf) {
        payload.person.cpf = payload.person.cpf.replace(/[^\d]/g, "");
        if (!payload.person.cpf) delete payload.person.cpf;
      }
    },
    async submitOmbudsman() {
      this.loading = true;
      try {
        const payload = JSON.parse(JSON.stringify(this.record));
        this.sanitizePayload(payload);

        const response = await sendOmbudsmanRecord(this.portal.slug, payload);
        if (response.status === 201) {
          const { protocol } = response.data;
          const files = this.$refs.upload.selectedFiles();
          this.context = {
            protocol,
          };
          if (files.length > 0) {
            this.uploading = true;
            this.currentPage = 1;

            this.upload.postAction = `/api/ombudsman/${this.portal.slug}/${protocol}/files`;
            for (const file of files) {
              file.postAction = this.upload.postAction;
              file.headers = this.upload.headers;
            }

            this.$nextTick(() => this.$refs.upload.start());
          } else {
            this.onOmbudsmanRecordCreated(protocol);
          }
        }
      } catch (error) {
        this.loading = false;
        if (
          error.response.status === 403 ||
          error.response.status === 400 ||
          error.response.status === 404
        ) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },

    async onUploadComplete() {
      this.uploadComplete = true;
      this.uploading = false;
      this.$nextTick(() => {
        this.currentPage = 2;
        this.$nextTick(() => {
          this.onOmbudsmanRecordCreated(this.context.protocol);
        });
      });
    },

    onOmbudsmanRecordCreated() {
      this.$bvToast.toast(this.$i18n.t("ombudsman.success"), {
        title: "Sucesso",
        toastClass: "custom-toast custom-toast-success",
        variant: "light",
        solid: true,
        autoHideDelay: 5000,
        noCloseButton: false,
      });
      this.loading = false;
      this.recordCreated = true;
    },
    async nextPage() {
      const next = () => {
        this.currentPage++;
        if (this.currentPage >= this.totalPages) {
          this.currentPage = 0;
        }
      };

      if (this.currentPage === 0) {
        let valid = await this.$validator.validateAll();
        const errors = this.errors.items.filter(
          (item) => !["termsAccepted", "lgpdAccepted"].includes(item.field)
        );
        valid = valid || errors.length === 0;
        this.infoValidated = valid;
        if (valid) {
          this.$nextTick(next);
        }
      } else if (this.currentPage === 1) {
        if (!this.$refs.upload.hasInvalidFiles) {
          next();
        }
      } else if (this.currentPage === 2) {
        if (await this.$validator.validateAll()) {
          await this.submitOmbudsman();
        }
      } else {
        next();
      }
    },
    previousPage() {
      this.currentPage--;
      if (this.currentPage < 0) {
        this.currentPage = this.totalPages - 1;
      }
    },
    validateState(ref) {
      if (
        this.vFields[ref] &&
        (this.vFields[ref].dirty || this.vFields[ref].validated)
      ) {
        return !this.errors.has(ref);
      }
      return null;
    },

    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>
